// FUNCTIONALITY-115A/115C-R1 — one external-action adapter for Telegram Mini App and browsers.
import { emitEvent, type StoreV2EventType } from "./events-v2.js";

export type ExternalActionType = "web" | "maps" | "instagram" | "email" | "phone" | "viber" | "telegram" | "copy";

export interface ExternalAction {
  type: ExternalActionType;
  value: string;
  copyFallback?: string;
  /** Optional analytics properties merged into channel / fallback events. */
  properties?: Record<string, unknown>;
  /** When true, skip default channel click event (caller emits richer payload). */
  skipAnalytics?: boolean;
}

export interface ExternalActionResult {
  kind: "opened" | "copied" | "fallback";
  message?: string;
  code?: "phone_unsupported" | "viber_unsupported" | "telegram_failed" | "generic";
}

type TelegramWebApp = {
  openLink?: (url: string) => void;
  openTelegramLink?: (url: string) => void;
  platform?: string;
};

const EVENT_BY_TYPE: Partial<Record<ExternalActionType, StoreV2EventType>> = {
  phone: "contact_phone_clicked",
  viber: "contact_viber_clicked",
  telegram: "contact_telegram_clicked",
  email: "contact_email_clicked",
  maps: "contact_map_clicked",
  instagram: "contact_instagram_clicked",
};

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const area = document.createElement("textarea");
      area.value = value;
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const copied = document.execCommand("copy");
      area.remove();
      return copied;
    } catch {
      return false;
    }
  }
}

export const INSTAGRAM_CANONICAL_URL = "https://www.instagram.com/asia.style.foods/";
export const STORE_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=%D0%B2%D1%83%D0%BB.%20%D0%91%D1%96%D0%BB%D0%BE%D0%B3%D0%BE%D1%80%D0%BE%D0%B4%D1%81%D1%8C%D0%BA%D0%B0%2C%2051%2C%20%D0%91%D0%BE%D1%8F%D1%80%D0%BA%D0%B0%2C%20%D0%9A%D0%B8%D1%97%D0%B2%D1%81%D1%8C%D0%BA%D0%B0%20%D0%BE%D0%B1%D0%BB%D0%B0%D1%81%D1%82%D1%8C%2C%2008150";

/** Staging-safe public store Telegram (exact t.me chat — not a share chooser). */
/** Production customer Mini App bot — COMMERCE-LAUNCH-167A-R10 SoT. */
export const STAGING_STORE_TELEGRAM_URL = "https://t.me/SkarbyAziiBot";
export const CUSTOMER_STORE_TELEGRAM_URL = "https://t.me/SkarbyAziiBot";

function telegramWebApp(): TelegramWebApp | undefined {
  return (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
}

export function isMobileExternalActionPlatform(userAgent: string, telegramPlatform?: string): boolean {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent) || /android|ios|iphone|ipad/i.test(telegramPlatform ?? "");
}

export function resolveHttpsAction(action: ExternalAction): string | null {
  const value = action.value.trim();
  if (action.type === "email") {
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(value)}`;
  }
  if (!/^https:\/\//i.test(value)) return null;
  return value;
}

function safeEmit(type: StoreV2EventType, properties: Record<string, unknown>): void {
  try {
    emitEvent(type, properties);
  } catch {
    /* analytics must never block external actions */
  }
}

async function fallback(
  action: ExternalAction,
  message: string,
  code: ExternalActionResult["code"] = "generic",
): Promise<ExternalActionResult> {
  const value = action.copyFallback?.trim() || action.value.trim();
  const copied = value ? await copyText(value) : false;
  safeEmit("contact_action_fallback_used", {
    ...(action.properties ?? {}),
    action_type: action.type,
    selected_contact_channel: action.type,
    fallback_used: true,
    copied,
  });
  return { kind: copied ? "copied" : "fallback", message, code };
}

/**
 * Open phone / Viber / Telegram / https without navigating the Mini App iframe
 * via window.location.href (uses window.open / Telegram.WebApp helpers only).
 */
/**
 * R5A — synchronous HTTPS open for payment handoff (must run inside the click stack).
 * Prefer Telegram.WebApp.openLink; never await network before calling this.
 */
export function openHttpsSync(url: string): { kind: "opened" | "failed"; via: string } {
  const value = String(url ?? "").trim();
  if (!/^https:\/\//i.test(value)) return { kind: "failed", via: "invalid_url" };
  try {
    const tg = telegramWebApp();
    if (tg?.openLink) {
      tg.openLink(value);
      return { kind: "opened", via: "telegram_openLink" };
    }
    const opened = window.open(value, "_blank", "noopener,noreferrer");
    if (opened) return { kind: "opened", via: "window_open" };
    // Same-tab navigation fallback for ordinary browsers (keeps user activation).
    window.location.assign(value);
    return { kind: "opened", via: "location_assign" };
  } catch {
    return { kind: "failed", via: "exception" };
  }
}

export async function openExternalAction(action: ExternalAction): Promise<ExternalActionResult> {
  const event = EVENT_BY_TYPE[action.type];
  if (event && !action.skipAnalytics) {
    safeEmit(event, {
      ...(action.properties ?? {}),
      action_type: action.type,
      selected_contact_channel: action.type,
      fallback_used: false,
    });
  }
  if (action.type === "copy") {
    return fallback(action, "Скопіюйте значення вручну.");
  }

  const tg = telegramWebApp();
  const value = action.value.trim();
  const mobile = isMobileExternalActionPlatform(navigator.userAgent || "", tg?.platform);

  try {
    if (action.type === "telegram") {
      // Exact t.me only — never a generic share chooser.
      if (!/^https:\/\/t\.me\//i.test(value)) {
        return fallback(action, "Telegram link unavailable.", "telegram_failed");
      }
      if (tg?.openTelegramLink) {
        tg.openTelegramLink(value);
        return { kind: "opened" };
      }
      // HTTPS t.me fallback (still exact chat URL).
      if (tg?.openLink) {
        tg.openLink(value);
        return { kind: "opened" };
      }
      const opened = window.open(value, "_blank", "noopener,noreferrer");
      if (opened) return { kind: "opened" };
      return fallback(action, "Telegram could not be opened.", "telegram_failed");
    }

    if (action.type === "phone" || action.type === "viber") {
      const digits = value.replace(/\D/g, "");
      const deepLink =
        action.type === "phone" ? `tel:${value}` : `viber://chat?number=%2B${digits}`;
      if (mobile) {
        // Direct user gesture → native scheme. OS chooser (Phone/Viber/…) is device-controlled.
        window.open(deepLink, "_blank");
        return { kind: "opened" };
      }
      // Desktop / unsupported: explicit copy fallback (caller shows localized message).
      return fallback(
        action,
        action.type === "phone" ? "Calling unsupported on this device." : "Viber unsupported on this device.",
        action.type === "phone" ? "phone_unsupported" : "viber_unsupported",
      );
    }

    if (action.type === "email" && mobile) {
      window.open(`mailto:${value}`, "_blank");
      return { kind: "opened" };
    }

    const httpsUrl = resolveHttpsAction(action);
    if (!httpsUrl) return fallback(action, "Посилання недоступне. Значення скопійовано.");
    if (tg?.openLink) {
      tg.openLink(httpsUrl);
      return { kind: "opened" };
    }
    const opened = window.open(httpsUrl, "_blank", "noopener,noreferrer");
    if (opened) return { kind: "opened" };
  } catch {
    // Visible/copy fallback is mandatory for blocked desktop/WebView actions.
  }
  return fallback(action, "Не вдалося відкрити дію. Значення скопійовано.");
}
